import { QUERY_PARAM_KEYS } from '@/constants';
import { localStorageKeys, localStorageValues } from '@/constants/localStorage';
import { trackEvent, amplitudeEventNames } from '@/features/amplitude';
import { endOfDay, isAfter } from 'date-fns';
import Swal from 'sweetalert2';

import styles from './index.module.scss';
import colors from '@/styles/constants/_colors.module.scss';

interface FireAlertProps {
  text: string;
  confirmButtonText?: string;
  confirmButtonColor?: string;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  padding?: string;
  thenCallback?: () => void;
  catchCallback?: () => void;
  finallyCallback?: () => void;
  reverseButtons?: boolean;
}

/**
 * alert 창을 표시
 * @param {string} props.text - alert 창의 안내 문구
 * @param {string} [props.confirmButtonText='확인'] - 확인 버튼에 표시할 텍스트, 기본값은 '확인'
 * @param {string} [props.confirmButtonColor=colors.black] - 확인 버튼의 색상, 기본값은 black
 * @param {boolean} [props.showCancelButton=false] - 취소 버튼을 표시할지 여부, 기본값은 false
 * @param {string} [props.cancelButtonText='취소'] - 취소 버튼에 표시할 텍스트, 기본값은 '취소'
 * @param {string} [props.padding='12px'] - 기본값은 '12px'
 * @param {Function} [props.thenCallback] - 확인 버튼 클릭 시 실행할 콜백 함수
 * @param {Function} [props.catchCallback] - 오류가 발생했을 때 실행할 콜백 함수
 * @param {Function} [props.finallyCallback] - alert 창이 닫힌 후 항상 실행할 콜백 함수
 */
export const fireAlert = ({
  text,
  confirmButtonText = '확인',
  confirmButtonColor = colors.n10,
  showCancelButton = false,
  cancelButtonText = '취소',
  padding = '12px',
  reverseButtons = false,
  thenCallback,
  catchCallback,
  finallyCallback,
}: FireAlertProps) => {
  Swal.fire({
    text,
    confirmButtonText,
    confirmButtonColor,
    cancelButtonText,
    padding,
    showCancelButton,
    reverseButtons,
  })
    .then((result) => {
      if (result.isConfirmed) {
        thenCallback && thenCallback();
      }
    })
    .catch(() => {
      catchCallback && catchCallback();
    })
    .finally(() => {
      finallyCallback && finallyCallback();
    });
};

interface FireAuthAlertProps {
  thenCallback: () => void;
}

export const fireSignupPopup = ({ thenCallback }: FireAuthAlertProps) => {
  Swal.fire({
    html: `
    <div class="${styles.content}">
      <div class="${styles.title}">
        내 투자 수익률 높이는
        <br/>
        더 많은 기능을 만나보세요!
      </div>
      <div class="${styles.subtitle}">
        3초면 회원가입 완료!
      </div>
    </div>
    `,
    confirmButtonText: '회원가입하기',
    cancelButtonText: '좀 더 둘러보기',
    showCancelButton: true,
    backdrop: 'rgba(0,0,0,0.8)',
    customClass: {
      popup: styles.signupPopup,
      htmlContainer: styles.htmlContainer,
      actions: styles.actions,
      confirmButton: styles.confirmButton,
      cancelButton: styles.cancelButton,
    },
  }).then((result) => {
    if (result.isConfirmed) {
      trackEvent(amplitudeEventNames.BTN_SIGNUP_IN_GUEST_MODE);
      thenCallback();
    }
  });
};

export const fireMultiRobotPopup = () => {
  Swal.fire({
    html: `
    <div class="${styles.content}">
      <img src="/images/il_multirobot-popup_320px.svg" alt="멀티로봇 일러스트" style="margin-bottom: 24px;" />
      <div class="${styles.title}">
        이제 여러대의 로봇을 동시에!
      </div>
      <div class="${styles.subtitle}">
        다양한 전략으로 분산 투자하면
        <br/>
        안정적인 수익을 유지할 수 있어요.
      </div>
    </div>
    `,
    confirmButtonText: '확인',
    showCancelButton: false,
    backdrop: 'rgba(0,0,0,0.8)',
    customClass: {
      popup: styles.multiRobotPopup,
      htmlContainer: styles.htmlContainer,
      actions: styles.actions,
      confirmButton: styles.confirmButton,
    },
  }).finally(() => {
    localStorage.setItem(
      localStorageKeys.MULTI_ROBOT_POPUP_SHOWN,
      localStorageValues.FINISHED,
    );
  });
};

interface FireEventPopupProps {
  thenCallback: () => void;
}

export const fireEventPopup = ({ thenCallback }: FireEventPopupProps) => {
  const _shouldShowEventPopup = (): boolean => {
    const hideUntil = localStorage.getItem(
      localStorageKeys.EVENT_POPUP_HIDE_UNTIL,
    );

    if (!hideUntil) {
      return true;
    }

    return isAfter(new Date(), new Date(hideUntil));
  };

  if (!_shouldShowEventPopup()) {
    return;
  }

  const CloseIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" style="cursor: pointer;" data-close="true">
      <path d="M21 7L7 21M21 21L7 7" stroke="#CFD0D3" stroke-width="2" stroke-linecap="round"/>
    </svg>
  `;

  const popup = Swal.fire({
    html: `
    <div>
      <div class="${styles.header}">
        ${CloseIcon}
      </div>
      <img 
        src="/images/event_home_popup.png" 
        alt="이벤트 일러스트"
        class="${styles.image}"
      />
    </div>
    `,
    showDenyButton: true,
    confirmButtonText: 'BTC 3만원 받으러 가기',
    denyButtonText: '오늘 하루 그만보기',
    backdrop: 'rgba(0,0,0,0.8)',
    padding: 0,
    width: 'auto',
    position: 'top',
    customClass: {
      popup: styles.eventPopup,
    },
  });

  document
    .querySelector('[data-close="true"]')
    ?.addEventListener('click', () => {
      Swal.close();
    });

  return popup.then((result) => {
    if (result.isConfirmed) {
      thenCallback();
      return;
    }

    if (result.isDenied) {
      const hideUntil = endOfDay(new Date());

      localStorage.setItem(
        localStorageKeys.EVENT_POPUP_HIDE_UNTIL,
        hideUntil.toISOString(),
      );
    }
  });
};

interface FireEventCompletePopupProps {
  cancelCallback: () => void;
}

export const fireEventCompletePopup = ({
  cancelCallback,
}: FireEventCompletePopupProps) => {
  const _shouldShowEventCompletePopup = () => {
    // query param에 popup=btc_event가 있는지 확인
    const url = new URL(window.location.href);
    return url.searchParams.get('popup') === QUERY_PARAM_KEYS.BTC_EVENT_POPUP;
  };

  if (!_shouldShowEventCompletePopup()) {
    return;
  }

  const CloseIcon = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g id="ic_close_">
      <path id="Icon" d="M18 6L6 18M18 18L6 6" stroke="#CFD0D3" stroke-width="2" stroke-linecap="round"/>
      </g>
    </svg>
  `;

  const ParticleIllust = `
    <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72" fill="none">
      <g clip-path="url(#clip0_7972_12135)">
        <path d="M11.0946 66.8592L53.058 51.777L18.4476 17.2854L3.43557 59.2092C1.73097 63.972 6.33537 68.5692 11.0946 66.8592Z" fill="#EF4452"/>
        <path d="M18.6417 8.39155C20.4987 8.39155 22.0041 6.88615 22.0041 5.02915C22.0041 3.17215 20.4987 1.66675 18.6417 1.66675C16.7847 1.66675 15.2793 3.17215 15.2793 5.02915C15.2793 6.88615 16.7847 8.39155 18.6417 8.39155Z" fill="#EDCCF8"/>
        <path d="M54.5927 50.8622C58.1988 47.2561 53.2417 36.4523 43.5206 26.7313C33.7996 17.0103 22.9959 12.0532 19.3898 15.6593C15.7837 19.2654 20.7408 30.0692 30.4618 39.7902C40.1828 49.5112 50.9866 54.4683 54.5927 50.8622Z" fill="#A41926"/>
        <path d="M30.7142 24.8526C30.5108 24.8526 30.302 24.8238 30.095 24.7662C28.8998 24.426 28.2086 23.1804 28.5488 21.9852C29.6216 18.2286 29.564 14.2614 28.3814 10.512C28.007 9.32583 28.6658 8.06402 29.8502 7.68962C31.0346 7.31522 32.2982 7.97222 32.6726 9.15842C34.1216 13.7502 34.1918 18.6138 32.876 23.22C32.5934 24.2082 31.6952 24.8526 30.7142 24.8526Z" fill="#B44BD7"/>
        <path d="M52.8666 31.4225C52.17 31.4225 51.4824 31.1003 51.0432 30.4919C50.3142 29.4857 50.5392 28.0799 51.5454 27.3491C56.3046 23.9003 61.9404 21.9401 67.8444 21.6827C69.0954 21.6701 70.1358 22.5917 70.1916 23.8319C70.2456 25.0739 69.2826 26.1233 68.0424 26.1791C63.0204 26.3987 58.2306 28.0637 54.186 30.9941C53.7864 31.2839 53.3256 31.4225 52.8684 31.4225H52.8666Z" fill="#EF4452"/>
        <path d="M61.8857 42.7786C61.5995 42.7786 61.3079 42.7228 61.0253 42.6058C59.5133 41.9794 57.9365 41.497 56.3381 41.1712C51.3467 40.1524 46.2077 40.6474 41.4773 42.6058C40.3235 43.0792 39.0113 42.5356 38.5379 41.389C38.0627 40.2406 38.6081 38.9248 39.7547 38.4496C45.3185 36.1456 51.3665 35.5624 57.2381 36.763C59.1173 37.1464 60.9713 37.7134 62.7479 38.4496C63.8963 38.9248 64.4417 40.2406 63.9665 41.389C63.6083 42.2548 62.7677 42.7786 61.8857 42.7786Z" fill="#7896FF"/>
        <path d="M38.4427 31.626C37.7677 31.626 37.0981 31.3236 36.6553 30.7458C35.8993 29.7612 36.0847 28.3482 37.0711 27.5922C42.4837 23.4396 45.9541 17.4276 46.8451 10.6632C47.0071 9.43197 48.1393 8.56257 49.3687 8.72637C50.6017 8.88837 51.4693 10.0188 51.3055 11.25C50.2579 19.206 46.1755 26.2764 39.8089 31.1616C39.4003 31.4748 38.9215 31.626 38.4427 31.626Z" fill="#4A81EC"/>
        <path d="M28.3143 35.5262L28.3151 32.1476C28.3153 31.4448 27.7457 30.8749 27.0428 30.8747L23.6642 30.8739C22.9614 30.8737 22.3915 31.4433 22.3913 32.1462L22.3905 35.5248C22.3903 36.2276 22.9599 36.7975 23.6628 36.7977L27.0414 36.7985C27.7442 36.7987 28.3141 36.229 28.3143 35.5262Z" fill="white"/>
        <path d="M42.5084 0.998629L39.4227 2.55503C38.781 2.87872 38.5231 3.66134 38.8468 4.30307L40.4032 7.38877C40.7269 8.03051 41.5095 8.28833 42.1513 7.96465L45.237 6.40825C45.8787 6.08456 46.1365 5.30194 45.8128 4.66021L44.2564 1.57451C43.9328 0.932773 43.1501 0.674944 42.5084 0.998629Z" fill="#7896FF"/>
        <path d="M67.2024 52.9242L68.2493 49.6306C68.467 48.9457 68.0883 48.2139 67.4033 47.9961L64.1097 46.9492C63.4247 46.7315 62.6929 47.1102 62.4752 47.7952L61.4283 51.0888C61.2105 51.7738 61.5893 52.5056 62.2743 52.7233L65.5679 53.7702C66.2529 53.988 66.9846 53.6092 67.2024 52.9242Z" fill="#A8BFFF"/>
        <path d="M60.3644 18.9251C61.8596 18.9251 63.0716 17.7131 63.0716 16.2179C63.0716 14.7228 61.8596 13.5107 60.3644 13.5107C58.8693 13.5107 57.6572 14.7228 57.6572 16.2179C57.6572 17.7131 58.8693 18.9251 60.3644 18.9251Z" fill="#FFC84D"/>
        <path d="M6.30779 30.6629C7.80293 30.6629 9.01499 29.4509 9.01499 27.9557C9.01499 26.4606 7.80293 25.2485 6.30779 25.2485C4.81264 25.2485 3.60059 26.4606 3.60059 27.9557C3.60059 29.4509 4.81264 30.6629 6.30779 30.6629Z" fill="#A8BFFF"/>
        <path d="M44.2147 65.9664C45.9593 65.9664 47.3737 64.5521 47.3737 62.8074C47.3737 61.0628 45.9593 59.6484 44.2147 59.6484C42.47 59.6484 41.0557 61.0628 41.0557 62.8074C41.0557 64.5521 42.47 65.9664 44.2147 65.9664Z" fill="#FFC84D"/>
      </g>
      <defs>
        <clipPath id="clip0_7972_12135">
          <rect width="72" height="72" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  `;

  const popup = Swal.fire({
    html: `
    <div class="${styles.content}">
      <div class="${styles.header}">
        <div class="${styles.closeButton}" data-close="true">
          ${CloseIcon}
        </div>
      </div>
      <div class="${styles.title}">
        <p class="${styles.p1}">
          이벤트 참여 완료!
        </p>
        <p class="${styles.p2}">
          보상 지급을 위해 계정 정보를 입력해주세요.
        </p>
      </div>
      <div class="${styles.illust}">
        ${ParticleIllust}
      </div>
      <div class="${styles.notice}">
        <div>
          ❉ 각각의 이벤트 참여 후, <strong>채널톡을 통해 이벤트 신청을 각각 제출</strong>해 주셔야 보상 지급이 가능합니다.
        </div>
        <div>
          ❉ 보상 지급일은 ‘이벤트 참여 계정 정보 제출 완료’일 기준 <strong>1개월 이내</strong>입니다.
        </div>
      </div>
    </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'BTC 받을 계정 입력하기',
    cancelButtonText: '이벤트 자세히보기',
    backdrop: 'rgba(0,0,0,0.8)',
    customClass: {
      popup: styles.eventCompletePopup,
      htmlContainer: styles.htmlContainer,
      actions: styles.actions,
      confirmButton: styles.confirmButton,
      cancelButton: styles.cancelButton,
    },
  });

  document
    .querySelector('[data-close="true"]')
    ?.addEventListener('click', () => {
      Swal.close();
    });

  return popup
    .then((result) => {
      if (result.isConfirmed) {
        window.open('https://tradingbank.channel.io/', '_blank');
        return;
      }

      if (result.dismiss === Swal.DismissReason.cancel) {
        cancelCallback();
      }
    })
    .finally(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('popup');
      window.history.replaceState({}, '', url.toString());
    });
};
